import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { capabilities } from '../data/capabilities';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { PartnerCTA } from '../components/PartnerCTA';
export function CapabilityPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const capability = capabilities.find(c => c.slug === slug);
  if (!capability) {
    return <Navigate to="/capabilities" replace data-id="element-402" />;
  }
  return <>
      <PageHero label="Capability Profile" title={capability.name} subtitle={capability.shortDescription} data-id="element-403" />

      <Breadcrumbs items={[{
      label: 'Manufacturing',
      to: '/manufacturing'
    }, {
      label: 'Our Processes',
      to: '/manufacturing/processes'
    }, {
      label: capability.name
    }]} data-id="element-404" />

      {/* Overview */}
      <section className="bg-white py-24" data-id="element-405">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-406">
          <div className="max-w-3xl" data-id="element-407">
            <h2 className="text-2xl font-bold text-navy-900 mb-6" data-id="element-408">Overview</h2>
            <p className="text-lg text-steel-600 leading-relaxed mb-12" data-id="element-409">
              {capability.description}
            </p>
          </div>

          {/* How It Works - Dossier Style */}
          <h3 className="text-xl font-bold text-navy-900 mb-8" data-id="element-410">
            Process Workflow
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-16" data-id="element-411">
            {capability.howItWorks.map(step => <div key={step.step} className="bg-white p-6 border border-steel-200 border-t-4 border-t-cg-orange-600" data-id="element-412">
                <span className="text-4xl font-black text-steel-200 mb-4 block" data-id="element-413">
                  0{step.step}
                </span>
                <h4 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-2" data-id="element-414">
                  {step.title}
                </h4>
                <p className="text-sm text-steel-600" data-id="element-415">{step.description}</p>
              </div>)}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-416">
            {/* Typical Outputs */}
            <div data-id="element-417">
              <h3 className="text-xl font-bold text-navy-900 mb-6" data-id="element-418">
                Typical Outputs
              </h3>
              <ul className="space-y-4" data-id="element-419">
                {capability.typicalOutputs.map((output, i) => <li key={i} className="flex items-center gap-3 p-4 bg-slate-50 border border-steel-100 rounded-sm" data-id="element-420">
                    <CheckCircle2 className="w-5 h-5 text-cg-orange-600" data-id="element-421" />
                    <span className="text-steel-700 font-medium" data-id="element-422">{output}</span>
                  </li>)}
              </ul>
            </div>

            {/* Supported Sectors */}
            <div data-id="element-423">
              <h3 className="text-xl font-bold text-navy-900 mb-6" data-id="element-424">
                Supported Sectors
              </h3>
              <div className="flex flex-wrap gap-3" data-id="element-425">
                {capability.supportedSectors.map(sectorSlug => <Link key={sectorSlug} to={`/sectors/${sectorSlug}`} className="px-4 py-2 bg-navy-900 text-white text-sm font-bold uppercase tracking-wider hover:bg-cg-orange-600 transition-colors rounded-sm" data-id="element-426">
                    {sectorSlug.charAt(0).toUpperCase() + sectorSlug.slice(1)}
                  </Link>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <PartnerCTA data-id="element-427" />
    </>;
}